import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Search,
  Loader2,
  RefreshCcw,
  Eye,
  ShieldAlert,
  ScrollText,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";

const PER_PAGE = 30;

function fmtFecha(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-MX", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  } catch {
    return iso;
  }
}

const ACTION_COLOR = (a) => {
  if (!a) return "bg-muted text-muted-foreground";
  if (a.startsWith("login") && a.includes("failed"))
    return "bg-rose-100 text-rose-800 border-rose-200";
  if (a.startsWith("login")) return "bg-emerald-100 text-emerald-800 border-emerald-200";
  if (a === "superadmin_access") return "bg-[#2563EB]/10 text-[#2563EB] border-[#2563EB]/30";
  if (a === "admin_update" || a === "admin_access")
    return "bg-blue-50 text-blue-700 border-blue-200";
  if (a === "acuerdo_firmado")
    return "bg-purple-100 text-purple-800 border-purple-200";
  if (a === "otp_failed" || a === "otp_locked")
    return "bg-rose-100 text-rose-800 border-rose-200";
  if (a === "data_update")
    return "bg-amber-100 text-amber-800 border-amber-200";
  return "bg-muted text-muted-foreground border-border";
};

export default function AdminAuditoria() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [inputQ, setInputQ] = useState("");
  const [action, setAction] = useState("__all__");
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");
  const [loading, setLoading] = useState(true);
  const [actions, setActions] = useState([]);
  const [selected, setSelected] = useState(null);

  // Guardia adicional en cliente
  useEffect(() => {
    if (authLoading) return;
    if (!user || !user.is_superadmin) {
      navigate("/admin", { replace: true });
    }
  }, [user, authLoading, navigate]);

  const load = useCallback(async () => {
    if (!user || !user.is_superadmin) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (action && action !== "__all__") params.set("action", action);
      if (desde) params.set("desde", desde);
      if (hasta) params.set("hasta", hasta);
      params.set("page", String(page));
      params.set("per_page", String(PER_PAGE));
      const { data } = await api.get(`/admin/auditoria?${params}`);
      setRows(data.items);
      setTotal(data.meta.total);
      setActions(data.actions || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [q, action, desde, hasta, page, user]);

  useEffect(() => {
    load();
  }, [load]);

  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));

  if (!user || !user.is_superadmin) {
    return (
      <div
        className="rounded-lg border border-border bg-muted/40 p-6 text-sm text-muted-foreground"
        data-testid="auditoria-no-permiso"
      >
        <ShieldAlert size={18} className="text-rose-600 inline-block mr-2" />
        Sólo cuentas superadmin pueden ver la auditoría.
      </div>
    );
  }

  return (
    <div data-testid="admin-auditoria">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="font-display text-xl font-semibold text-[#0F2942] flex items-center gap-2">
            <ScrollText size={18} className="text-[#2563EB]" />
            Auditoría
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            {total} evento{total === 1 ? "" : "s"} en el audit log inmutable. Cada consulta genera
            su propio audit (superadmin_access, op=read_audit).
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={load}
          disabled={loading}
          className="gap-1.5 self-start"
          data-testid="auditoria-refrescar-btn"
        >
          <RefreshCcw size={14} /> Refrescar
        </Button>
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          setPage(1);
          setQ(inputQ.trim());
        }}
        className="mt-4 grid grid-cols-1 sm:grid-cols-[1fr_180px_140px_140px_auto] items-end gap-2"
      >
        <div>
          <label className="block text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">
            Buscar (email / nombre del actor)
          </label>
          <div className="relative">
            <Search
              size={14}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"
            />
            <Input
              value={inputQ}
              onChange={(e) => setInputQ(e.target.value)}
              placeholder="daniela@..."
              className="pl-9 h-9"
              data-testid="auditoria-buscar-input"
            />
          </div>
        </div>
        <div>
          <label className="block text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">
            Acción
          </label>
          <Select
            value={action}
            onValueChange={(v) => {
              setPage(1);
              setAction(v);
            }}
          >
            <SelectTrigger
              data-testid="auditoria-action-select"
              className="h-9"
            >
              <SelectValue placeholder="Todas" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Todas las acciones</SelectItem>
              {actions.map((a) => (
                <SelectItem key={a} value={a} data-testid={`auditoria-action-opt-${a}`}>
                  {a}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="block text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">
            Desde
          </label>
          <Input
            type="date"
            value={desde}
            onChange={(e) => {
              setPage(1);
              setDesde(e.target.value);
            }}
            className="h-9"
            data-testid="auditoria-desde-input"
          />
        </div>
        <div>
          <label className="block text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">
            Hasta
          </label>
          <Input
            type="date"
            value={hasta}
            onChange={(e) => {
              setPage(1);
              setHasta(e.target.value);
            }}
            className="h-9"
            data-testid="auditoria-hasta-input"
          />
        </div>
        <div className="flex items-center gap-1.5">
          <Button type="submit" size="sm" variant="outline" data-testid="auditoria-buscar-btn">
            Buscar
          </Button>
          {(q || action !== "__all__" || desde || hasta) && (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => {
                setInputQ("");
                setQ("");
                setAction("__all__");
                setDesde("");
                setHasta("");
                setPage(1);
              }}
              data-testid="auditoria-limpiar-btn"
            >
              Limpiar
            </Button>
          )}
        </div>
      </form>

      <div className="mt-4 border border-border rounded-lg bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="auditoria-tabla">
            <thead className="bg-muted/40 text-[11px] uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="text-left px-4 py-2 font-semibold">Fecha</th>
                <th className="text-left px-4 py-2 font-semibold">Actor</th>
                <th className="text-left px-4 py-2 font-semibold">Acción</th>
                <th className="text-left px-4 py-2 font-semibold">Recurso</th>
                <th className="text-left px-4 py-2 font-semibold">IP</th>
                <th className="text-left px-4 py-2 font-semibold">Metadata</th>
                <th className="text-right px-4 py-2 font-semibold">Detalle</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">
                    <Loader2 size={14} className="inline-block animate-spin mr-2" /> Cargando…
                  </td>
                </tr>
              )}
              {!loading && rows.length === 0 && (
                <tr>
                  <td
                    colSpan={7}
                    className="px-4 py-8 text-center text-muted-foreground"
                    data-testid="auditoria-vacio"
                  >
                    Sin eventos para los criterios de búsqueda.
                  </td>
                </tr>
              )}
              {!loading &&
                rows.map((r) => (
                  <tr
                    key={r.id}
                    className="border-t border-border hover:bg-muted/30"
                    data-testid={`auditoria-row-${r.id}`}
                  >
                    <td className="px-4 py-2 whitespace-nowrap font-mono text-[11px]">
                      {fmtFecha(r.timestamp)}
                    </td>
                    <td className="px-4 py-2 min-w-[180px]">
                      <div className="font-medium text-[#0F2942] text-xs break-all">
                        {r.actor_email}
                      </div>
                      {r.actor_role && (
                        <div className="text-[10px] text-muted-foreground uppercase tracking-wider">
                          {r.actor_role === "paciente"
                            ? "persona usuaria"
                            : r.actor_role === "medico"
                            ? "especialista"
                            : r.actor_role}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-2">
                      <span
                        className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium font-mono ${ACTION_COLOR(
                          r.action
                        )}`}
                      >
                        {r.action || "—"}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-xs">
                      <div className="font-mono">{r.resource_type || "—"}</div>
                      {r.resource_id && (
                        <div className="text-[10px] text-muted-foreground font-mono break-all">
                          {r.resource_id}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-2 text-xs text-muted-foreground font-mono">
                      {r.ip_truncada || "—"}
                    </td>
                    <td className="px-4 py-2 text-[11px] text-muted-foreground max-w-[280px]">
                      <div className="truncate" title={r.metadata_resumen}>
                        {r.metadata_resumen || "—"}
                      </div>
                    </td>
                    <td className="px-4 py-2 text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 w-7 p-0"
                        onClick={() => setSelected(r)}
                        data-testid={`auditoria-ver-btn-${r.id}`}
                        title="Ver evento completo"
                      >
                        <Eye size={13} />
                      </Button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      {totalPages > 1 && (
        <div
          className="mt-4 flex items-center justify-between text-xs text-muted-foreground"
          data-testid="auditoria-paginacion"
        >
          <div>
            Página {page} de {totalPages} · {total} evento{total === 1 ? "" : "s"}
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              disabled={page <= 1 || loading}
              onClick={() => setPage(page - 1)}
              data-testid="auditoria-prev-btn"
            >
              Anterior
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={page >= totalPages || loading}
              onClick={() => setPage(page + 1)}
              data-testid="auditoria-next-btn"
            >
              Siguiente
            </Button>
          </div>
        </div>
      )}

      <Dialog
        open={selected !== null}
        onOpenChange={(open) => (open ? null : setSelected(null))}
      >
        <DialogContent
          className="max-w-2xl max-h-[85vh] overflow-y-auto"
          data-testid="auditoria-detalle-modal"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#0F2942]">
              <ScrollText size={16} className="text-[#2563EB]" /> Evento de auditoría
            </DialogTitle>
            <DialogDescription>Registro append-only del audit_log inmutable.</DialogDescription>
          </DialogHeader>
          {selected && (
            <div className="space-y-2 text-sm">
              <Row k="ID" v={selected.id} mono />
              <Row k="Fecha" v={selected.timestamp} mono />
              <Row k="Actor" v={`${selected.actor_email} (${selected.actor_role || "—"})`} />
              <Row k="Actor ID" v={selected.actor_id || "—"} mono />
              <Row k="Acción" v={selected.action} mono />
              <Row k="Recurso tipo" v={selected.resource_type || "—"} mono />
              <Row k="Recurso ID" v={selected.resource_id || "—"} mono />
              <Row k="IP truncada" v={selected.ip_truncada || "—"} mono />
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">
                  Metadata (JSON)
                </div>
                <pre className="rounded-md bg-muted/40 border border-border p-3 text-[11px] font-mono overflow-auto max-h-64">
                  {JSON.stringify(selected.metadata_full || {}, null, 2)}
                </pre>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelected(null)}>
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({ k, v, mono }) {
  return (
    <div className="grid grid-cols-[140px_1fr] gap-2 items-start">
      <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        {k}
      </div>
      <div
        className={`text-foreground break-all ${mono ? "font-mono text-[11px]" : "text-xs"}`}
      >
        {v || "—"}
      </div>
    </div>
  );
}
